import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../app/hooks';
import { selectActiveSuggestions } from '../../redux/features/aiSuggestions/aiSuggestions.selector';
import { dismissSuggestion } from '../../redux/features/aiSuggestions/aiSuggestions.slice';
import { IAISuggestion } from '../../redux/features/aiSuggestions/aiSuggestions.props';
import useAgentInterception from '../../hooks/agentInterception/useAgentInterception.hooks';
import { sendSpokenPrompt } from '../../reducers/spokenPromptThunk';
import useAutocompleteMessage from '../OmniBar/hooks/useAutocompleteMessage';
import {
  AgentInterceptionType,
  messagingActions,
} from '../../reducers/messagingSlice';
import {
  selectFeatureFlagHitl3,
  selectIsAISuggestionAutoAddEnabled,
} from '../../redux/features/config/config.selector';
import {
  selectBrandMenuConstraints,
  selectBrandMenuItemsByName,
  selectTopLevelMenuItems,
} from '../../selectors/menu';
import { selectOrderValues } from '../../selectors/order';
import { selectActiveRestaurantDetails } from '../../selectors/restaurant';
import { addItemToCart } from '../../reducers/menuSlice';
import { removeSlashCommandByIntent } from '../../reducers/dialogSlice';
import {
  getSlashCommandIntent,
  isCartAddingUpsellSuggestion,
  resolveUpsellSlashItem,
} from '../../utils/aiSuggestionUpsell';
import { hasSpeakTotalInUpsellCondition } from '../../utils/cart.upsellConditions';
import { resolveTtsPlaceholders } from '../../utils/TTSMessage';
import logger from '../../utils/logger';

/**
 * Accept / dismiss logic for AI suggestions, extracted so the card buttons and
 * the Alt+A / Alt+D keyboard shortcuts drive the exact same path. The human
 * agent stays the driver — a suggestion is only ever applied on an explicit
 * accept.
 */
const useAISuggestionActions = () => {
  const dispatch = useAppDispatch();
  const suggestions = useSelector(selectActiveSuggestions);
  const isHitl3 = useSelector(selectFeatureFlagHitl3);
  // HITL-1429: hitl_ai_suggestion_auto_add && hitl_3_0 && agent_cart. Gates
  // only whether an accepted upsell also lands in the cart (acceptUpsell) —
  // placeholder handling is NOT behind it: every accept speaks through the
  // shared sendSpokenPrompt thunk (HITL-1587), which never lets a raw {total}
  // out, flag or no flag.
  const isAutoAddEnabled = useSelector(selectIsAISuggestionAutoAddEnabled);
  const topLevelMenuItems = useSelector(selectTopLevelMenuItems);
  const brandMenuItemsByName = useSelector(selectBrandMenuItemsByName);
  const brandMenuConstraints = useSelector(selectBrandMenuConstraints);
  const { total } = useSelector(selectOrderValues);
  const { restaurantName, restaurantCode } = useSelector(
    selectActiveRestaurantDetails
  );
  const { sendAgentInterception } = useAgentInterception();
  // HITL-1429: the omnibar's own send path, reused verbatim so an accepted
  // upsell behaves identically to an accepted autoprompt (see acceptUpsell).
  // Safe to instantiate here — the hook holds only the omnibar's local input
  // text in useState and runs no effects, so nothing fires on mount.
  const { sendDonationPromptMessage } = useAutocompleteMessage();

  // Only the latest suggestion is ever displayed; treat it as the active one
  // that the keyboard shortcuts act on.
  const activeSuggestion: IAISuggestion | undefined =
    suggestions[suggestions.length - 1];

  /**
   * HITL-1429: the agent-facing text for a suggestion — the same string the
   * customer will hear. Brand-menu prompts carry `{total}` /
   * `{RESTAURANT_NAME}` placeholders (the AI passes the menu's prompt through
   * verbatim), and the card used to render them raw, so the agent read
   * "Your total is {total}" on screen while the omnibar showed the real figure.
   *
   * HITL-1587: no longer behind the auto-add flag — the accept path now
   * always speaks through the shared sendSpokenPrompt thunk (never a raw
   * placeholder), so the card resolves unconditionally to keep matching what
   * would be spoken.
   */
  const getDisplayText = (suggestion: IAISuggestion | undefined): string => {
    if (!suggestion) return '';
    return resolveTtsPlaceholders({
      message: suggestion.title,
      total,
      restaurantCode,
      restaurantName,
    });
  };

  /**
   * Emit the HITL -> AI response the backend consumes to close the co-pilot
   * loop. hitl_3_0 only. The envelope (event/agent_type/session_id) is stamped
   * by the messaging middleware; we only supply `data`. `verbatim` is always
   * true today — the accepted line is spoken unchanged (no edit-before-speak
   * flow yet); placeholder substitution is mechanical, not an agent edit, so it
   * does not make the line non-verbatim.
   *
   * `spokenText` is what actually went out, so the AI's record matches what the
   * customer heard. Omitted entirely when nothing was spoken — an upsell
   * skipped for want of a check total is still `accepted`, but carries no
   * spoken_text, which is the shape the no-tts case has always emitted.
   */
  const emitSuggestionResponse = (
    suggestion: IAISuggestion,
    action: 'accepted' | 'dismissed',
    spokenText?: string
  ) => {
    if (!isHitl3) return;
    dispatch(
      messagingActions.sendAgentSuggestionResponse({
        data: {
          action,
          suggestion_id: suggestion.suggestion_id,
          request_id: suggestion.request_id,
          ...(action === 'accepted' && spokenText
            ? { spoken_text: spokenText, verbatim: true }
            : null),
        },
      })
    );
  };

  const clearSuggestion = (suggestion: IAISuggestion) => {
    dispatch(
      dismissSuggestion({
        suggestionType: suggestion.suggestion_type,
        suggestionId: suggestion.suggestion_id,
      })
    );
  };

  /**
   * HITL-1429 — accept an upsell the AI offered, with exact parity to accepting
   * the omnibar autoprompt for the same upsell.
   *
   * Parity is the whole design, and it is achieved by CALLING the omnibar's own
   * `sendDonationPromptMessage` rather than reimplementing what it does. That
   * one call covers all four behaviours the autoprompt has, so none of them can
   * drift out of step with it:
   *   - substitutes `{total}` / `{RESTAURANT_NAME}` before speaking;
   *   - stamps TTS_SENT;
   *   - records the total as spoken (with a cart fingerprint) so Complete does
   *     not read it out a second time (HITL-1570);
   *   - returns false when the prompt is gated on a check total that does not
   *     exist yet, in which case nothing is spoken.
   *
   * A false return means the upsell was never offered, so — exactly as on the
   * autoprompt path — it must not land in the cart either. It is still consumed
   * from the queue and the card is still cleared, so the agent moves on.
   */
  const acceptUpsell = (suggestion: IAISuggestion): void => {
    const upsell = suggestion.metadata?.upsell;
    const resolvedItem = resolveUpsellSlashItem({
      topLevelMenuItems,
      brandMenuItemsByName,
      upsell,
    });

    // Prefer the resolved item's flag — that is literally the value the
    // autoprompt would pass. When nothing resolved, fall back to the condition
    // on the constraint the AI itself named: the same datum read one hop
    // earlier (the item's flag is derived from it), so the degraded path still
    // cannot speak a raw `{total}`.
    const speakTotalInUpsell = resolvedItem
      ? resolvedItem.speakTotalInUpsell
      : hasSpeakTotalInUpsellCondition(
          upsell?.constraint_name
            ? brandMenuConstraints[upsell.constraint_name]?.conditions
            : undefined
        );

    const wasSpoken = sendDonationPromptMessage(
      suggestion.tts ?? '',
      speakTotalInUpsell,
      { intent: 'ai_suggestion' }
    );

    if (!wasSpoken) {
      logger.warn({
        message: `AI upsell suggestion accepted but skipped — no check total for a speak_total_in_upsell prompt: ${upsell?.constraint_name} (${suggestion.suggestion_id})`,
      });
    } else if (!resolvedItem) {
      // A normal outcome, not an error: plenty of AI-fireable upsell
      // constraints have no slash-command carrier for the agent to add. Loud
      // because a silent miss is indistinguishable from the bug this ticket
      // fixes.
      logger.warn({
        message: `AI upsell suggestion accepted but no agent-reachable item carries constraint "${upsell?.constraint_name}" — spoken only, nothing added (${suggestion.suggestion_id})`,
        moreInfo: JSON.stringify(upsell),
      });
    } else {
      // byPassAutoPrompt: the line has already been spoken above, so the thunk
      // must not speak it again — this is what keeps the customer from hearing
      // the upsell twice (AC2). The constraint is deliberately left unfilled:
      // the tts is a question, and the customer has not answered it yet.
      dispatch(
        addItemToCart({
          menuItem: resolvedItem,
          quantity: 1,
          byPassAutoPrompt: true,
        })
      );
      sendAgentInterception({
        data: {
          type: AgentInterceptionType.ORDER_CHANGED,
          description: `${resolvedItem.name} added to cart`,
        },
      });
      logger.info({
        message: `AI upsell suggestion accepted: added ${resolvedItem.name} for constraint "${upsell?.constraint_name}" (${suggestion.suggestion_id})`,
      });
    }

    // AC5: this upsell is done with, however it went — spoken and carted,
    // spoken only, or skipped. Drop it from the slash-command queue so Enter
    // does not offer the same thing again. Keyed off the resolved item's own
    // name, never a hardcoded intent; a no-op when the queue never held it.
    if (resolvedItem) {
      dispatch(removeSlashCommandByIntent(getSlashCommandIntent(resolvedItem)));
    }

    emitSuggestionResponse(
      suggestion,
      'accepted',
      wasSpoken
        ? resolveTtsPlaceholders({
            message: suggestion.tts ?? '',
            total,
            restaurantCode,
            restaurantName,
          })
        : undefined
    );
    clearSuggestion(suggestion);
  };

  const accept = (suggestion: IAISuggestion) => {
    // HITL-1429: an upsell the agent can actually complete in the cart takes
    // the parity path above. Everything else — every other suggestion_type, the
    // AI's mid-order and end-of-order upsell stages, and the whole hook with
    // the flag off — keeps today's speak-only behaviour.
    const isCartAdding = isCartAddingUpsellSuggestion(suggestion);
    // Every previous attempt at this ticket failed SILENTLY: the classifier
    // missed and accept quietly fell through to speak-only, which is
    // indistinguishable from the bug the ticket exists to fix. So the decision
    // inputs are logged on every accept — this is the line to read first when
    // "accepting the suggestion added nothing".
    logger.debug({
      message: `AI suggestion accept gate: cartAdd=${
        isAutoAddEnabled && isCartAdding
      } (flag=${isAutoAddEnabled}, classified=${isCartAdding}) type=${
        suggestion.suggestion_type
      } verdict=${suggestion.verdict} constraint=${
        suggestion.metadata?.upsell?.constraint_name ?? 'none'
      }`,
    });
    if (isAutoAddEnabled && isCartAdding) {
      acceptUpsell(suggestion);
      return;
    }

    let spokenText: string | undefined;
    if (suggestion.tts) {
      // Any suggestion that carries a `tts` line is speakable, regardless of
      // its suggestion_type. HITL-1587: speak it via the shared spoken-prompt
      // thunk — the same substitution, no-total skip, and spoken-total
      // bookkeeping as every other send path — so a raw `{total}` can never
      // go out from here (the backend's suggestion tts carries the menu
      // template verbatim). The suggestion is still consumed on a skip, like
      // a gated donation accept; `spoken_text` is simply omitted, the shape
      // the no-tts case has always emitted.
      const { spoken, message } = dispatch(
        sendSpokenPrompt({
          message: suggestion.tts,
          metadata: { intent: 'ai_suggestion' },
          skipMetricMessage: `AI suggestion (${suggestion.suggestion_id}) not spoken: no check total for its {total} template`,
        })
      );
      if (spoken) {
        spokenText = message ?? undefined;
        sendAgentInterception({
          data: { type: AgentInterceptionType.TTS_SENT },
        });
        logger.info({
          message: `AI suggestion accepted (tts): "${spokenText}" (${suggestion.suggestion_id})`,
        });
      } else {
        logger.warn({
          message: `AI suggestion accepted but skipped — no check total for its {total}-templated tts (${suggestion.suggestion_id})`,
        });
      }
    } else {
      logger.info({
        message: `AI suggestion accepted: ${suggestion.suggestion_type} (${suggestion.suggestion_id})`,
        moreInfo: JSON.stringify(suggestion.action),
      });
    }
    emitSuggestionResponse(suggestion, 'accepted', spokenText);
    clearSuggestion(suggestion);
  };

  const dismiss = (suggestion: IAISuggestion) => {
    logger.info({
      message: `AI suggestion dismissed: ${suggestion.suggestion_type} (${suggestion.suggestion_id})`,
    });
    emitSuggestionResponse(suggestion, 'dismissed');
    clearSuggestion(suggestion);
  };

  return { suggestions, activeSuggestion, accept, dismiss, getDisplayText };
};

export default useAISuggestionActions;
